import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { CalendarRange, ChevronLeft, ChevronRight, Loader2, Package, User, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  confirmed: "bg-emerald-400/90 text-emerald-950 border-emerald-600",
  active: "bg-sky-400/90 text-sky-950 border-sky-600",
  pending: "bg-amber-400/90 text-amber-950 border-amber-600",
  pending_payment: "bg-amber-300/80 text-amber-950 border-amber-500",
  completed: "bg-slate-400/70 text-slate-950 border-slate-500",
  cancelled: "bg-red-300/70 text-red-950 border-red-500 line-through opacity-70",
};

function pad(n) { return n.toString().padStart(2, "0"); }
function toYMD(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function addDays(d, n) { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; }
function dayLabel(d) { return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit" }); }
function monthTitle(d) { return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); }

export default function RentalsCalendarAdmin() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [grid, setGrid] = useState(null);

  const dates = useMemo(() => Array.from({ length: days }).map((_, i) => addDays(anchor, i)), [anchor, days]);

  useEffect(() => {
    api.get("/products").then(r => {
      const items = (Array.isArray(r.data) ? r.data : (r.data?.items || [])).filter(x => x.is_rental && x.is_published);
      setProducts(items);
      if (items[0]) setProductId(items[0].id);
    }).catch(() => setProducts([]));
  }, []);

  const load = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const start = toYMD(anchor);
      const end = toYMD(addDays(anchor, days));
      const r = await api.get(`/admin/rentals/calendar?product_id=${productId}&start=${start}&end=${end}`);
      setGrid(r.data);
    } catch (e) {
      toast.error("Erreur chargement planning");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [productId, anchor, days]); // eslint-disable-line

  // For each unit × day, find bookings overlapping that day (returns array of bookings per unit/day).
  const overlaps = (b, day) => {
    const dStart = new Date(day); dStart.setHours(0, 0, 0, 0);
    const dEnd = addDays(dStart, 1);
    const bStart = new Date(b.start_at);
    const bEnd = new Date(b.end_at);
    return bStart < dEnd && bEnd > dStart;
  };

  // compute contiguous bars: for each unit, group bookings into bars spanning start/end
  const computeBars = (units) => {
    if (!units) return [];
    return units.map(u => {
      const bars = (u.bookings || []).map(b => {
        const bStart = new Date(b.start_at);
        const bEnd = new Date(b.end_at);
        const startIdx = Math.max(0, Math.floor((bStart - anchor) / (1000 * 60 * 60 * 24)));
        const endIdx = Math.min(days, Math.ceil((bEnd - anchor) / (1000 * 60 * 60 * 24)));
        return { ...b, startIdx, endIdx, span: Math.max(1, endIdx - startIdx) };
      }).filter(b => b.endIdx > 0 && b.startIdx < days);
      return { ...u, bars };
    });
  };
  const unitsWithBars = useMemo(() => computeBars(grid?.units || []), [grid, anchor, days]);

  const activeProduct = products.find(p => p.id === productId);

  return (
    <div className="p-6 md:p-10 space-y-4" data-testid="rentals-calendar-admin">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Planning des locations</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Vue calendrier par unité — style Airbnb.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger className="w-64" data-testid="calendar-product-select"><SelectValue placeholder="Produit"/></SelectTrigger>
            <SelectContent>
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({(p.rental_units || []).length} unités)</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-36"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 jours</SelectItem>
              <SelectItem value="14">14 jours</SelectItem>
              <SelectItem value="30">30 jours</SelectItem>
              <SelectItem value="60">60 jours</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Button size="sm" variant="outline" onClick={() => setAnchor(addDays(anchor, -days))} data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4 mr-1"/>Précédent
          </Button>
          <div className="font-semibold text-[color:var(--brand-navy)] capitalize">{monthTitle(anchor)} — {days} jours</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setAnchor(d); }} data-testid="calendar-today">Aujourd'hui</Button>
            <Button size="sm" variant="outline" onClick={() => setAnchor(addDays(anchor, days))} data-testid="calendar-next">
              Suivant<ChevronRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
        </div>

        {!productId ? (
          <div className="py-16 text-center text-[hsl(var(--muted-foreground))]">
            <CalendarRange className="w-12 h-12 mx-auto opacity-30 mb-3"/>
            <p>Sélectionnez un produit pour afficher son planning.</p>
          </div>
        ) : loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[color:var(--brand-sky)]"/></div>
        ) : unitsWithBars.length === 0 ? (
          <div className="py-16 text-center text-[hsl(var(--muted-foreground))]">
            <Package className="w-12 h-12 mx-auto opacity-30 mb-3"/>
            <p className="font-semibold">Aucune unité configurée pour ce produit.</p>
            <p className="text-sm mt-1">Ajoutez des unités (ex: "Vélo #1", "Chambre Rose") dans l'éditeur produit.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="calendar-grid">
            <div className="min-w-[900px]">
              {/* Header dates row */}
              <div className="grid" style={{ gridTemplateColumns: `220px repeat(${days}, minmax(50px, 1fr))` }}>
                <div className="py-2 px-3 font-semibold text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">Unité</div>
                {dates.map((d, i) => {
                  const today = toYMD(d) === toYMD(new Date());
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <div key={i} className={`py-2 px-1 text-center text-xs font-medium border-b border-[hsl(var(--border))] ${today ? "bg-sky-50 text-sky-700 font-bold" : weekend ? "text-slate-500 bg-slate-50" : ""}`}>
                      <div className="capitalize">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</div>
                      <div className="text-[10px]">{d.getDate()}/{d.getMonth() + 1}</div>
                    </div>
                  );
                })}
              </div>
              {/* Unit rows */}
              {unitsWithBars.map(u => (
                <div key={u.id} className="grid relative border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30 transition-colors" style={{ gridTemplateColumns: `220px repeat(${days}, minmax(50px, 1fr))` }} data-testid={`calendar-row-${u.id}`}>
                  <div className="py-3 px-3 font-semibold text-sm text-[color:var(--brand-navy)] sticky left-0 bg-[hsl(var(--card))] z-10">
                    <div className="flex items-center gap-1.5">
                      {!u.is_active && <span className="w-1.5 h-1.5 rounded-full bg-red-400"/>}
                      {u.is_active && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/>}
                      <span className="truncate">{u.name || "—"}</span>
                    </div>
                    {u.notes && <div className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">{u.notes}</div>}
                  </div>
                  {/* Empty day cells (background) */}
                  {dates.map((_, i) => <div key={i} className="h-14 border-l border-[hsl(var(--border))]/30"/>)}
                  {/* Booking bars overlay */}
                  <div className="absolute top-1 bottom-1 pointer-events-none" style={{ left: "220px", right: 0 }}>
                    <div className="relative h-full">
                      {u.bars.map(b => {
                        const cellWidth = `calc((100% / ${days}) * ${b.span})`;
                        const leftPos = `calc((100% / ${days}) * ${b.startIdx})`;
                        const cls = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
                        return (
                          <div
                            key={b.id}
                            className={`absolute top-1 bottom-1 pointer-events-auto border rounded-md px-2 py-1 text-[11px] font-semibold overflow-hidden shadow-sm cursor-pointer ${cls}`}
                            style={{ left: leftPos, width: cellWidth }}
                            title={`${b.customer_name} · ${b.status}`}
                            data-testid={`calendar-bar-${b.id}`}
                          >
                            <div className="flex items-center gap-1 truncate">
                              <User className="w-3 h-3 shrink-0"/>
                              <span className="truncate">{b.customer_name}</span>
                            </div>
                            <div className="text-[9px] opacity-80 flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5"/>{b.duration} {b.duration_unit === "hour" ? "h" : b.duration_unit === "night" ? "n" : "j"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              {Object.entries(STATUS_COLORS).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className={`inline-block w-3 h-3 rounded ${v.split(" ").filter(c => c.startsWith("bg-")).join(" ")}`}/>
                  <span className="capitalize">{k.replace("_", " ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
